/* «Сменить голос в записи» (mode other) и «Почистить запись» (mode keep) — один экран, режим задаёт вход (APP-SPEC-5 §6; 03-api.md §9.2 `voice`).
   Форма сверху вниз: шапка с ценой за минуту, рамка загрузки звука (файл или запись с микрофона, до предела политики поля), «Прослушать запись»,
   «Голос ›» (только «Сменить голос», только голоса с переозвучкой), «Убрать шум», тихая подсказка про отправку, кнопка с ценой.
   Ключи заказа — только опубликованные формой: audio (или audio_upload_id), voice_id, denoise, mode; чего форма не публикует, на экране нет. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { SwitchRow } from '../../ui/SwitchRow';
import { shake } from '../../ui/motion';
import { sampleFile } from '../clone/sample-file';
import { useRecorder } from '../clone/use-recorder';
import { recordingWav } from '../tts/recording-wav';
import type { StudioApp } from '../studio/studio-api';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioSheet } from '../studio/StudioSheet';
import { StudioUploadRights } from '../studio/StudioUploadRights';
import { useStudioForm } from '../studio/use-studio-form';
import { ToolCreateButton } from '../studio/circle/ToolCreateButton';
import { ToolHeader } from '../studio/circle/ToolHeader';
import { leaveTail, useTailRetry } from '../studio/circle/tool-helpers';
import { VoiceLine, VoiceListen, VoiceUpload } from './VoiceParts';
import { VoiceTail } from './VoiceTail';
import { useVoicePicker } from './use-voice-picker';
import {
  DEFAULT_MAX_MS,
  actLabel,
  audioField,
  audioLimits,
  denoiseDefault,
  denoiseField,
  filePeaks,
  isOn,
  missText,
  modeField,
  modeTitle,
  mmss,
  onValue,
  voiceField,
  voiceMissing,
  voiceMode,
  voiceOut,
  voicePrice,
  voiceUnit,
} from './voice-model';
import './voice.css';
import './native-voice.css';

interface Source {
  owner: number | undefined;
  /** id загрузки, к которой относится файл; null — загрузка ещё идёт */
  uploadId: string | null;
  file: File;
  durationMs: number | null;
  peaks: number[] | null;
  recorded: boolean;
}
const AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/webm'];

export function VoiceOrderForm({
  app,
  avatar,
  initialInputs,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
}) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form, inputs, locked, setInputs } = state;
  const retry = useTailRetry(state);
  const mode = voiceMode(form, initialInputs);
  const audio = audioField(form);
  const key = audio?.key ?? 'audio';
  const vField = mode === 'other' ? voiceField(form) : undefined;
  const nField = denoiseField(form);
  const mField = modeField(form);
  const maxMs = audio?.upload?.max_duration_ms ?? DEFAULT_MAX_MS;
  const maxBytes = audio?.upload?.max_bytes ?? 20 * 1024 * 1024;
  const owner = state.ownerId;
  const audioId = inputs[key];
  const frame = useRef<HTMLDivElement>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [queued, setQueued] = useState<Source | null>(null);
  const [reading, setReading] = useState(false);
  const [say, setSay] = useState('');
  const [consent, setConsent] = useState<{ file: File; recorded: boolean } | null>(null);
  const replace = useRef(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const generation = useRef(0);

  /* режим и «Убрать шум» по умолчанию — только в опубликованные поля */
  useEffect(() => {
    if (!form || locked) return;
    setInputs(previous => {
      let next = previous;
      if (mField && previous[mField.key] !== mode) next = { ...next, [mField.key]: mode };
      if (nField && previous[nField.key] === undefined) next = { ...next, [nField.key]: denoiseDefault(nField, mode, initialInputs) };
      return next;
    });
  }, [form, locked, mode, mField, nField, initialInputs, setInputs]);

  /* выбранный файл встаёт за загрузкой, когда прежняя убрана (upload читает uploadIds из своего замыкания) */
  const { upload, clearUpload, uploadIds, orderId } = state;
  useEffect(() => {
    if (!queued || locked || uploadIds[key] || inputs[key]) return;
    const next = queued;
    void Promise.resolve().then(() => setQueued(value => (value === next ? null : value)));
    void upload(key, next.file);
  }, [queued, locked, uploadIds, inputs, key, upload]);
  /* «Заменить запись» с отказа проверки: форма вернулась — запись уходит */
  useEffect(() => {
    if (!replace.current || orderId || locked) return;
    replace.current = false;
    clearUpload(key);
  }, [orderId, locked, clearUpload, key]);

  /* файл в рамке: загрузка, к которой он относится; выбранный только что (uploadId null) — пока идёт и после неё его загрузка */
  const file = source && source.owner === owner && (source.uploadId === null || source.uploadId === audioId) ? source : null;
  /* запись из повтора («История», «Изменить»): превью загрузки с сервера, длина и волна — из самого файла */
  useEffect(() => {
    if (!audioId || !owner || file || source?.uploadId === null) return;
    const request = new AbortController();
    const deadline = window.setTimeout(() => {
      request.abort();
      setPreviewError(true);
    }, 30000);
    void (async () => {
      const me = await endpoints.me();
      if (me.user.id !== owner) throw new Error('owner_changed');
      const blob = await endpoints.uploadPreview(audioId, request.signal);
      if (request.signal.aborted) return;
      if (!AUDIO_TYPES.includes(blob.type) || blob.size > maxBytes) throw new Error('audio_preview_invalid');
      const current = await endpoints.me();
      if (request.signal.aborted) return;
      if (current.user.id !== owner) throw new Error('owner_changed');
      const restored = new File([blob], uiText('Запись'), { type: blob.type });
      setPreviewError(false);
      setSource({ owner, uploadId: audioId, file: restored, durationMs: null, peaks: null, recorded: false });
      const ms = await sampleFile.readDurationMs(restored, request.signal).catch(() => null);
      const wave = await filePeaks(restored, undefined, request.signal);
      if (request.signal.aborted) return;
      setSource(value =>
        value?.file === restored ? { ...value, durationMs: ms ?? wave?.durationMs ?? null, peaks: wave?.peaks ?? null } : value,
      );
    })()
      .catch(() => {
        if (!request.signal.aborted) setPreviewError(true);
      })
      .finally(() => clearTimeout(deadline));
    return () => {
      clearTimeout(deadline);
      request.abort();
    };
  }, [audioId, owner, file, source?.uploadId, maxBytes, previewAttempt]);

  const refuse = (message: string) => {
    setSay(message);
    shake(frame.current);
  };
  async function pick(raw: File, recorded = false) {
    if (locked || !audio?.upload) return;
    if (!state.rights) {
      setConsent({ file: raw, recorded });
      return;
    }
    const current = ++generation.current;
    setSay('');
    let chosen: File;
    try {
      chosen = recorded ? raw : sampleFile.confirmAudioType(raw);
    } catch {
      refuse(audioLimits(audio, maxMs));
      return;
    }
    if (!audio.upload.accept.includes(chosen.type)) return refuse(audioLimits(audio, maxMs));
    if (chosen.size > maxBytes) return refuse(uiFormat('Файл больше {0} МБ, загрузите меньше', Math.round(maxBytes / 1024 / 1024)));
    setReading(true);
    const ms = await sampleFile.readDurationMs(chosen).catch(() => null);
    if (generation.current !== current) return;
    setReading(false);
    if (!ms) return refuse('Файл не читается, загрузите другой');
    if (ms > maxMs) return refuse(uiFormat('Длиннее {0}, загрузите короче', mmss(maxMs / 1000)));
    if (ms < 1000) return refuse('Запись слишком короткая');
    const next: Source = { owner, uploadId: null, file: chosen, durationMs: ms, peaks: null, recorded };
    if (state.uploadIds[key] || inputs[key]) state.clearUpload(key);
    setSource(next);
    setQueued(next);
    void filePeaks(chosen).then(wave => {
      if (wave) setSource(value => (value?.file === chosen ? { ...value, peaks: wave.peaks } : value));
    });
  }
  const recorder = useRecorder((recordedFile: File) => {
    void recordingWav(recordedFile, maxMs)
      .then(wav => pick(new File([wav], uiText('Запись с микрофона') + '.wav', { type: 'audio/wav' }), true))
      .catch(() => refuse('Не удалось прочитать запись, попробуйте ещё раз'));
  }, maxMs);
  useEffect(() => {
    if (locked && recorder.state !== 'idle') recorder.cancel();
  }, [locked, recorder]);

  /* «Прослушать запись» под рамкой */
  const player = useRef<HTMLAudioElement>(null);
  const [play, setPlay] = useState<{ url: string; on: boolean; pos: number }>({ url: '', on: false, pos: 0 });
  const url = useMemo(
    () => (file && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file.file) : ''),
    [file?.file], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  const playing = play.url === url && play.on;
  const position = play.url === url ? play.pos : 0;
  const setPlaying = (on: boolean) => setPlay(value => ({ url, on, pos: value.url === url ? value.pos : 0 }));
  const setPosition = (pos: number) => setPlay(value => ({ url, on: value.url === url && value.on, pos }));
  const toggle = () => {
    const element = player.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => setPlaying(false));
    else element.pause();
  };

  const picker = useVoicePicker({
    value: state.voice,
    disabled: locked,
    onPick: voice => {
      if (!vField) return;
      state.setVoice(voice);
      setInputs(previous => ({ ...previous, [vField.key]: String(voice.id) }));
    },
  });
  const denoise = nField ? isOn(inputs[nField.key]) : false;
  const durationMs = file?.durationMs ?? null;
  const missingKind = voiceMissing({
    recording: recorder.state !== 'idle',
    hasFile: !!file || reading,
    uploaded: !!inputs[key],
    durationMs: reading ? null : durationMs,
    maxMs,
    needsVoice: !!vField,
    voice: vField ? (inputs[vField.key] ? state.voice : null) : null,
  });
  const price = voicePrice(app, form, durationMs);
  const unit = voiceUnit(app, form);
  /* хвост — по входу заказа: его режим и шумоподавление */
  const sentInputs = (state.intent?.body.inputs ?? {}) as Record<string, unknown>;
  const tailMode = (['other', 'keep'] as const).find(value => value === sentInputs.mode) ?? mode;
  const tailDenoise = nField && sentInputs[nField.key] !== undefined ? isOn(String(sentInputs[nField.key])) : denoise;

  return (
    <section className="native-studio-form native-voice st-in ap-in vc-in">
      <ToolHeader title={modeTitle(state.orderId ? tailMode : mode)} unit={unit || undefined} />
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <p role="alert" className="vc-err">
          {uiText(state.error)}{' '}
          <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <>
          <div id="vcBody" className="pf-body vc-body">
            {audio?.upload && (
              <div className="vc-up" id="vcUpW">
                <VoiceUpload
                  ref={frame}
                  file={file ? { name: file.file.name, size: file.file.size, durationMs: file.durationMs, peaks: file.peaks, recorded: file.recorded } : null}
                  title="Загрузите запись"
                  sub={mode === 'other' ? 'Ваша речь зазвучит выбранным голосом' : 'Голосовое, интервью или заметку: уберём шум'}
                  note={audioLimits(audio, maxMs)}
                  say={say}
                  accept={audio.upload.accept}
                  disabled={locked}
                  rec={{ state: recorder.state, elapsedMs: recorder.elapsed, maxMs }}
                  progress={playing || position > 0 ? (file?.durationMs ? position / file.durationMs : 0) : null}
                  onPick={next => void pick(next)}
                  onRecord={() => {
                    setSay('');
                    player.current?.pause();
                    void recorder.start();
                  }}
                  onRecordStop={recorder.stop}
                  onRecordCancel={recorder.cancel}
                  onRemove={() => {
                    generation.current++;
                    state.clearUpload(key);
                    setSource(null);
                    setQueued(null);
                    setSay('');
                  }}
                />
                {recorder.error && <p role="alert">{uiText(recorder.error)}</p>}
                <div className="vc-lsw" id="vcLsW">
                  {file && file.durationMs && recorder.state === 'idle' && (
                    <>
                      {url && (
                        <audio
                          ref={player}
                          src={url}
                          preload="metadata"
                          hidden
                          onPlay={() => setPlaying(true)}
                          onPause={() => setPlaying(false)}
                          onEnded={() => {
                            setPlaying(false);
                            setPosition(0);
                          }}
                          onTimeUpdate={event => setPosition(Math.round(event.currentTarget.currentTime * 1000))}
                        />
                      )}
                      <VoiceListen
                        name={file.file.name}
                        recorded={file.recorded}
                        durationMs={file.durationMs}
                        positionMs={position}
                        playing={playing}
                        onToggle={toggle}
                      />
                    </>
                  )}
                </div>
                {source && source.uploadId === null && !queued && !inputs[key] && !state.busy && (
                  <button
                    type="button"
                    className="ar-lnk"
                    disabled={locked || !state.rights}
                    onClick={() => void state.upload(key, state.uploadIds[key] ? null : source.file)}
                  >
                    {uiText('Продолжить загрузку')}
                  </button>
                )}
                {audioId && previewError && !file && (
                  <p role="alert">
                    {uiText('Не удалось открыть запись для прослушивания.')}{' '}
                    <button type="button" onClick={() => setPreviewAttempt(value => value + 1)}>
                      {uiText('Повторить')}
                    </button>
                  </p>
                )}
              </div>
            )}
            {vField && (
              <>
                <VoiceLine voice={inputs[vField.key] ? state.voice : null} disabled={locked} busy={picker.busy} onOpen={picker.open} />
                {picker.error && <p role="alert">{uiText(picker.error)}</p>}
              </>
            )}
            {nField && (
              <div className="vc-nz" id="vcNz">
                <SwitchRow
                  title={uiText('Убрать шум')}
                  description={uiText(mode === 'other' ? 'Перед сменой голоса уберём фон и эхо' : 'Оставим только голос, без фона и эха')}
                  icon={<StudioIcon kind="denoise" />}
                  checked={denoise}
                  disabled={locked}
                  onChange={checked => setInputs(previous => ({ ...previous, [nField.key]: onValue(nField, checked) }))}
                />
              </div>
            )}
            <p className="vc-hint">
              <StudioIcon kind="send" />
              {uiText('Голосовым или файлом: выберете, когда запись будет готова')}
            </p>
          </div>
          <ToolCreateButton
            label={actLabel(mode, denoise)}
            price={price.price}
            from={price.from}
            chargeable={form.chargeable !== false}
            missing={missingKind ? missText(missingKind, maxMs) : null}
            busy={state.busy && !!state.intent}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            available={!!form.available}
            onCreate={() => void state.submit()}
          />
        </>
      )}
      {state.orderId && (
        <VoiceTail
          state={state}
          mode={tailMode}
          denoise={tailDenoise}
          out={voiceOut(tailMode, initialInputs, tailMode === 'other' || !nField || tailDenoise)}
          voice={state.voice}
          onRetry={retry}
          onReplace={() =>
            void leaveTail(state).then(ok => {
              if (!ok) return;
              replace.current = true;
              setSource(null);
            })
          }
        />
      )}
      {consent && (
        <StudioSheet title="Права на материалы" onClose={() => setConsent(null)}>
          <h2>{uiText('Права на материалы')}</h2>
          <StudioUploadRights state={state} />
          <button
            type="button"
            className="btn"
            disabled={locked || !state.rights}
            onClick={() => {
              const next = consent;
              setConsent(null);
              void pick(next.file, next.recorded);
            }}
          >
            {uiText('Продолжить загрузку')}
          </button>
        </StudioSheet>
      )}
    </section>
  );
}
